import { Toast } from '../../../../components/Common/Toast';
import { callAPI } from '../../../../services/api.service';
import { HTTPStatusEnum } from '../../../../interfaces/log.interface';

export interface PrintBatchOrderEntry {
  orderNumber: string;
  ownerEmail: string;
  orderId: string;
  bookId: string | null;
  bookTitle: string | null;
  quantity: number | null;
  pdfUrl: string | null;
  shipstationOrderId: string | null;
  result?: 'ok' | 'failed';
  resultError?: string;
}

export interface PrintBatch {
  id: string;
  batchNumber: number;
  status: 'draft' | 'dispatched' | 'acknowledged' | 'partially_failed' | 'failed' | 'canceled';
  createdBy: string;
  createdAt: any;
  dispatchedAt?: any;
  acknowledgedAt?: any;
  orders: PrintBatchOrderEntry[];
  orderCount: number;
  dispatchAttempts: Array<{ at: any; by: string; ok: boolean; error?: string }>;
  lastError?: string;
}

export const fetchPrintBatches = async (): Promise<PrintBatch[]> => {
  try {
    const response = await callAPI('admin/print-batches');
    return response?.batches || [];
  } catch {
    Toast('Failed to load print batches', true);
    return [];
  }
};

export const createPrintBatch = async (
  orders: Array<{ ownerEmail: string; orderNumber: string }>,
  loader: (v: boolean) => void,
): Promise<{ ok: boolean; batchId?: string; rejected?: Array<{ orderNumber: string; reason: string }> }> => {
  try {
    loader(true);
    const response = await callAPI('admin/print-batches', { orders }, 'POST');
    if (response?.status === HTTPStatusEnum.OK) {
      Toast(response?.message || 'Print job created');
      (response?.rejected || []).forEach((r: any) => Toast(`${r.orderNumber}: ${r.reason}`, true));
      return { ok: true, batchId: response?.batchId, rejected: response?.rejected };
    }
    Toast(response?.error || 'Failed to create batch', true);
    (response?.rejected || []).forEach((r: any) => Toast(`${r.orderNumber}: ${r.reason}`, true));
    return { ok: false, rejected: response?.rejected };
  } catch {
    Toast('Failed to create batch', true);
    return { ok: false };
  } finally {
    loader(false);
  }
};

export const dispatchPrintBatch = async (batchId: string, loader: (v: boolean) => void): Promise<boolean> => {
  try {
    loader(true);
    const response = await callAPI(`admin/print-batches/${batchId}/dispatch`, {}, 'POST');
    if (response?.status === HTTPStatusEnum.OK) {
      Toast(response?.message || 'Batch dispatched');
      return true;
    }
    Toast(response?.error || 'Dispatch failed', true);
    return false;
  } catch {
    Toast('Dispatch failed', true);
    return false;
  } finally {
    loader(false);
  }
};

export const cancelPrintBatch = async (batchId: string, loader: (v: boolean) => void): Promise<boolean> => {
  try {
    loader(true);
    const response = await callAPI(`admin/print-batches/${batchId}/cancel`, {}, 'POST');
    if (response?.status === HTTPStatusEnum.OK) {
      Toast(response?.message || 'Batch canceled');
      return true;
    }
    Toast(response?.error || 'Cancel failed', true);
    return false;
  } catch {
    Toast('Cancel failed', true);
    return false;
  } finally {
    loader(false);
  }
};

export const setDispatchHold = async (
  email: string,
  orderNumber: string,
  hold: boolean,
  loader: (v: boolean) => void,
): Promise<boolean> => {
  try {
    loader(true);
    const response = await callAPI('admin/orders/dispatch-hold', { email, orderNumber, hold }, 'POST');
    if (response?.status === HTTPStatusEnum.OK) {
      Toast(response?.message || 'Hold updated');
      return true;
    }
    Toast(response?.error || 'Failed to update hold', true);
    return false;
  } catch {
    Toast('Failed to update hold', true);
    return false;
  } finally {
    loader(false);
  }
};
